import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangleIcon, ArrowDownNarrowWideIcon, ArrowLeftIcon, ArrowRightIcon, CalendarIcon, CheckIcon, ChevronDownIcon, FilterIcon, ListIcon, XIcon, Rows3Icon, SearchIcon, UserRoundIcon } from 'lucide-react';
import { CaseType, mockTeamMembers, Priority, Status, Ticket } from '../data/mockData';
import { TicketDetailModal } from './TicketDetailModal';
type TicketFilters = {
  status: Status[];
  priority: Priority[];
  assignee: string[];
};
type ClosedFilters = {
  createdBy: string[];
  closedDate: string[];
};
type MultiSelectOption = {
  value: string;
  label: string;
};
type BoardColumn = Status | CaseType;
type TicketView = 'active' | 'closed' | 'types';
type Density = 'comfortable' | 'compact';
type SortMode = 'triage' | 'newest';
const workingSetSize = 8;
const workflowStatuses: Status[] = ['New', 'Working', 'Waiting for Response', 'On Hold', 'Escalated', 'Closed'];
const caseTypes: CaseType[] = ['DNC', 'Create Contact', 'Laptop Request', 'Business Cards'];
const priorityRank: Record<Priority, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3
};
const columnStyles: Record<BoardColumn, {
  accent: string;
  dot: string;
  count: string;
  empty: string;
}> = {
  New: {
    accent: 'border-blue-200',
    dot: 'bg-blue-500',
    count: 'bg-blue-100 text-blue-700',
    empty: 'text-blue-700'
  },
  Working: {
    accent: 'border-cyan-200',
    dot: 'bg-cyan-500',
    count: 'bg-cyan-100 text-cyan-700',
    empty: 'text-cyan-700'
  },
  'Waiting for Response': {
    accent: 'border-purple-200',
    dot: 'bg-purple-500',
    count: 'bg-purple-100 text-purple-700',
    empty: 'text-purple-700'
  },
  'On Hold': {
    accent: 'border-amber-200',
    dot: 'bg-amber-500',
    count: 'bg-amber-100 text-amber-700',
    empty: 'text-amber-700'
  },
  Escalated: {
    accent: 'border-red-200',
    dot: 'bg-red-500',
    count: 'bg-red-100 text-red-700',
    empty: 'text-red-700'
  },
  DNC: {
    accent: 'border-slate-200',
    dot: 'bg-slate-500',
    count: 'bg-slate-200 text-slate-700',
    empty: 'text-slate-700'
  },
  'Create Contact': {
    accent: 'border-indigo-200',
    dot: 'bg-indigo-500',
    count: 'bg-indigo-100 text-indigo-700',
    empty: 'text-indigo-700'
  },
  'Laptop Request': {
    accent: 'border-teal-200',
    dot: 'bg-teal-500',
    count: 'bg-teal-100 text-teal-700',
    empty: 'text-teal-700'
  },
  Closed: {
    accent: 'border-emerald-200',
    dot: 'bg-emerald-500',
    count: 'bg-emerald-100 text-emerald-700',
    empty: 'text-emerald-700'
  },
  'Business Cards': {
    accent: 'border-rose-200',
    dot: 'bg-rose-500',
    count: 'bg-rose-100 text-rose-700',
    empty: 'text-rose-700'
  }
};
const priorityStyles: Record<Priority, string> = {
  Critical: 'border-red-200 bg-red-50 text-red-700',
  High: 'border-orange-200 bg-orange-50 text-orange-700',
  Medium: 'border-amber-200 bg-amber-50 text-amber-700',
  Low: 'border-emerald-200 bg-emerald-50 text-emerald-700'
};
const initialVisibleCounts: Record<BoardColumn, number> = {
  New: workingSetSize,
  Working: workingSetSize,
  'Waiting for Response': workingSetSize,
  'On Hold': workingSetSize,
  Escalated: workingSetSize,
  DNC: workingSetSize,
  'Create Contact': workingSetSize,
  'Laptop Request': workingSetSize,
  Closed: workingSetSize,
  'Business Cards': workingSetSize
};
type TicketTableProps = {
  tickets: Ticket[];
  onTicketsChange: React.Dispatch<React.SetStateAction<Ticket[]>>;
};
export function TicketTable({
  tickets,
  onTicketsChange
}: TicketTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [activeTab, setActiveTab] = useState<TicketView>('active');
  const [density, setDensity] = useState<Density>('comfortable');
  const [sortMode, setSortMode] = useState<SortMode>('triage');
  const [visibleCounts, setVisibleCounts] = useState(initialVisibleCounts);
  const [draggingTicketId, setDraggingTicketId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<BoardColumn | null>(null);
  const [filters, setFilters] = useState<TicketFilters>({
    status: [],
    priority: [],
    assignee: []
  });
  const [closedFilters, setClosedFilters] = useState<ClosedFilters>({
    createdBy: [],
    closedDate: []
  });
  const activeStatuses = workflowStatuses.filter(status => status !== 'Closed');
  const visibleColumns: BoardColumn[] = activeTab === 'closed' ? ['Closed'] : activeTab === 'types' ? caseTypes : filters.status.length > 0 ? activeStatuses.filter(status => filters.status.includes(status)) : activeStatuses;
  const draggedTicket = tickets.find(ticket => ticket.id === draggingTicketId);
  const activeTicketCount = tickets.filter(ticket => ticket.status !== 'Closed' && ticket.status !== 'On Hold' && !ticket.caseType).length;
  const closedTicketCount = tickets.filter(ticket => ticket.status === 'Closed' && !ticket.caseType).length;
  const typeCaseCount = tickets.filter(ticket => ticket.caseType).length;
  const assignees = useMemo(() => [...new Set(['Unassigned', ...mockTeamMembers.map(member => member.name), ...tickets.map(ticket => ticket.assignedTo)])].sort(), [tickets]);
  const closedCreators = useMemo(() => [...new Set(tickets.filter(ticket => ticket.status === 'Closed' && !ticket.caseType).map(ticket => ticket.submittedBy))].sort(), [tickets]);
  const filteredTickets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const sortTickets = (firstTicket: Ticket, secondTicket: Ticket) => {
      if (activeTab !== 'closed' && sortMode === 'triage') {
        const priorityDifference = priorityRank[firstTicket.priority] - priorityRank[secondTicket.priority];
        if (priorityDifference !== 0) return priorityDifference;
      }
      const firstDate = activeTab === 'closed' ? firstTicket.closedDate : firstTicket.createdDate;
      const secondDate = activeTab === 'closed' ? secondTicket.closedDate : secondTicket.createdDate;
      return new Date(secondDate ?? secondTicket.createdDate).getTime() - new Date(firstDate ?? firstTicket.createdDate).getTime();
    };
    return tickets.filter(ticket => {
      if (activeTab === 'closed') return ticket.status === 'Closed' && !ticket.caseType;
      if (activeTab === 'types') return Boolean(ticket.caseType);
      return ticket.status !== 'Closed' && !ticket.caseType;
    }).filter(ticket => !query || ticket.subject.toLowerCase().includes(query) || ticket.id.toLowerCase().includes(query) || ticket.submittedBy.toLowerCase().includes(query)).filter(ticket => activeTab !== 'active' || filters.status.length === 0 || filters.status.includes(ticket.status)).filter(ticket => activeTab === 'closed' || filters.priority.length === 0 || filters.priority.includes(ticket.priority)).filter(ticket => activeTab === 'closed' || filters.assignee.length === 0 || filters.assignee.includes(ticket.assignedTo)).filter(ticket => activeTab !== 'closed' || closedFilters.createdBy.length === 0 || closedFilters.createdBy.includes(ticket.submittedBy)).filter(ticket => activeTab !== 'closed' || closedFilters.closedDate.length === 0 || closedFilters.closedDate.includes(ticket.closedDate?.slice(0, 10) ?? '')).sort(sortTickets);
  }, [activeTab, closedFilters, filters, searchQuery, sortMode, tickets]);
  const ticketsByColumn = useMemo(() => [...workflowStatuses, ...caseTypes].reduce<Record<BoardColumn, Ticket[]>>((groups, column) => ({
    ...groups,
    [column]: filteredTickets.filter(ticket => isCaseType(column) ? ticket.caseType === column : ticket.status === column)
  }), {
    New: [],
    Working: [],
    'Waiting for Response': [],
    'On Hold': [],
    Escalated: [],
    Closed: [],
    DNC: [],
    'Create Contact': [],
    'Laptop Request': [],
    'Business Cards': []
  }), [filteredTickets]);
  useEffect(() => {
    setVisibleCounts(initialVisibleCounts);
  }, [activeTab, closedFilters, filters, searchQuery, sortMode]);
  useEffect(() => {
    setVisibleCounts(currentCounts => [...workflowStatuses, ...caseTypes].reduce<Record<BoardColumn, number>>((nextCounts, column) => ({
      ...nextCounts,
      [column]: Math.min(Math.max(workingSetSize, currentCounts[column]), ticketsByColumn[column].length)
    }), initialVisibleCounts));
  }, [ticketsByColumn]);
  const applyStatus = (ticket: Ticket, status: Status): Ticket => {
    const now = new Date().toISOString();
    const wasOnHold = ticket.status === 'On Hold';
    const isOnHold = status === 'On Hold';
    const onHoldPeriods = isOnHold && !wasOnHold ? [...(ticket.onHoldPeriods ?? []), {
      startedAt: now
    }] : wasOnHold && !isOnHold ? closeOpenHoldPeriods(ticket.onHoldPeriods, now) : ticket.onHoldPeriods;
    return {
      ...ticket,
      status,
      onHoldPeriods,
      closedDate: status === 'Closed' ? ticket.closedDate ?? now : undefined
    };
  };
  const updateTicket = (updatedTicket: Ticket) => {
    const existingTicket = tickets.find(ticket => ticket.id === updatedTicket.id);
    const normalizedTicket = existingTicket ? applyStatus({
      ...existingTicket,
      ...updatedTicket,
      status: existingTicket.status,
      onHoldPeriods: existingTicket.onHoldPeriods
    }, updatedTicket.status) : updatedTicket;
    onTicketsChange(currentTickets => currentTickets.map(ticket => ticket.id === normalizedTicket.id ? normalizedTicket : ticket));
    setSelectedTicket(normalizedTicket);
  };
  const moveTicket = (ticket: Ticket, direction: -1 | 1) => {
    const currentIndex = workflowStatuses.indexOf(ticket.status);
    const nextStatus = workflowStatuses[currentIndex + direction];
    if (nextStatus) {
      onTicketsChange(currentTickets => currentTickets.map(currentTicket => currentTicket.id === ticket.id ? applyStatus(currentTicket, nextStatus) : currentTicket));
    }
  };
  const moveCaseType = (ticket: Ticket, direction: -1 | 1) => {
    const currentIndex = caseTypes.indexOf(ticket.caseType as CaseType);
    const nextType = caseTypes[currentIndex + direction];
    if (nextType) {
      onTicketsChange(currentTickets => currentTickets.map(currentTicket => currentTicket.id === ticket.id ? {
        ...currentTicket,
        caseType: nextType
      } : currentTicket));
    }
  };
  const handleDragStart = (event: React.DragEvent<HTMLElement>, ticket: Ticket) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ticket.id);
    setDraggingTicketId(ticket.id);
  };
  const handleDragOver = (event: React.DragEvent<HTMLElement>, column: BoardColumn) => {
    if (!draggedTicket || getTicketColumn(draggedTicket, activeTab) === column) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
  };
  const handleDrop = (event: React.DragEvent<HTMLElement>, column: BoardColumn) => {
    event.preventDefault();
    const ticketId = event.dataTransfer.getData('text/plain') || draggingTicketId;
    const ticket = tickets.find(currentTicket => currentTicket.id === ticketId);
    if (ticket && getTicketColumn(ticket, activeTab) !== column) {
      onTicketsChange(currentTickets => currentTickets.map(currentTicket => {
        if (currentTicket.id !== ticket.id) return currentTicket;
        return activeTab === 'types' ? {
          ...currentTicket,
          caseType: column as CaseType
        } : applyStatus(currentTicket, column as Status);
      }));
    }
    setDraggingTicketId(null);
    setDragOverColumn(null);
  };
  const clearDragState = () => {
    setDraggingTicketId(null);
    setDragOverColumn(null);
  };
  const updateVisibleCount = (column: BoardColumn, count: number) => {
    setVisibleCounts(currentCounts => ({
      ...currentCounts,
      [column]: count
    }));
  };
  const activeFilterCount = activeTab === 'closed' ? closedFilters.createdBy.length + closedFilters.closedDate.length : activeTab === 'types' ? filters.priority.length + filters.assignee.length : filters.status.length + filters.priority.length + filters.assignee.length;
  return <>
      <section aria-labelledby="ticket-workspace-title" className="w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-153" data-mp="10fxbwv7l3pf9">
        <header className="border-b border-gray-100 p-5" data-id="element-154" data-mp="vindwr64q5ft">
          <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center" data-id="element-155" data-mp="qosxbwltm6ky">
            <div data-id="element-156" data-mp="lrsurvo6jlyl">
              <h2 id="ticket-workspace-title" className="text-lg font-bold text-gray-900" data-id="element-157" data-mp="lrao053w5pcf">
                Ticket Management Workspace
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-158" data-mp="cpjlta8k80nb">
                Triage the highest-impact cases first, then work through each
                queue.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-159" data-mp="27uizqzdlot9g">
              <div className="relative" data-id="element-160" data-mp="2bv2ww2y6bwvl">
                <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" data-id="element-161" data-mp="2bwxf39i29avz" />
                <input type="search" aria-label="Search tickets" placeholder="Search tickets..." className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-4 text-sm text-gray-900 outline-none transition-shadow placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 sm:w-60" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} data-id="element-162" data-mp="230odppaiw277" />
              </div>
              <button type="button" aria-expanded={showFilters} aria-controls="ticket-filters" onClick={() => setShowFilters(isVisible => !isVisible)} className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showFilters ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-163" data-mp="39gig0uulh7ve">
                <FilterIcon aria-hidden="true" className="h-4 w-4" data-id="element-164" data-mp="b5ncs9c55xrn" />
                Filters
                {activeFilterCount > 0 && <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-bold text-white" data-id="element-165" data-mp="69q2ajnk7lms">
                    {activeFilterCount}
                  </span>}
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-4 border-t border-gray-100 pt-4 lg:flex-row lg:items-end lg:justify-between" data-id="element-166" data-mp="2lxzrerb54cqr">
            <div className="flex items-center gap-1" role="tablist" aria-label="Ticket views" data-id="element-167" data-mp="2hyqy1nk0igvq">
              <button type="button" role="tab" aria-selected={activeTab === 'active'} aria-controls="active-kanban-board" onClick={() => setActiveTab('active')} className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeTab === 'active' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-slate-100 hover:text-gray-900'}`} data-id="element-168" data-mp="kxtq5vo5f47s">
                Active cases <span className="ml-1 opacity-80" data-id="element-169" data-mp="1dlzbajj46v85">{activeTicketCount}</span>
              </button>
              <button type="button" role="tab" aria-selected={activeTab === 'types'} aria-controls="type-cases-board" onClick={() => setActiveTab('types')} className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeTab === 'types' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-slate-100 hover:text-gray-900'}`} data-id="element-170" data-mp="1ijipjjftb803">
                Type cases <span className="ml-1 opacity-80" data-id="element-171" data-mp="2y5kzef9sjz2j">{typeCaseCount}</span>
              </button>
              <button type="button" role="tab" aria-selected={activeTab === 'closed'} aria-controls="closed-kanban-board" onClick={() => setActiveTab('closed')} className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeTab === 'closed' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-slate-100 hover:text-gray-900'}`} data-id="element-172" data-mp="3vt34ng48uj1s">
                Closed cases <span className="ml-1 opacity-80" data-id="element-173" data-mp="3mtn9t5y16x6b">{closedTicketCount}</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3" data-id="element-174" data-mp="3931xk0331lhe">
              {activeTab !== 'closed' && <label className="flex items-center gap-2 text-sm font-medium text-gray-600" htmlFor="ticket-sort" data-id="element-175" data-mp="3d3c2raj1w7v7">
                  <ArrowDownNarrowWideIcon aria-hidden="true" className="h-4 w-4 text-gray-400" data-id="element-176" data-mp="9n51tbs0soq4" />
                  Sort
                  <select id="ticket-sort" value={sortMode} onChange={event => setSortMode(event.target.value as SortMode)} className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-500" data-id="element-177" data-mp="3rtt6j0j5r9v9">
                    <option value="triage" data-id="element-178" data-mp="2lal7p3d3sp7j">Triage: priority, then newest</option>
                    <option value="newest" data-id="element-179" data-mp="2cu44blwcoc1h">Newest first</option>
                  </select>
                </label>}

              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" aria-label="Card density" data-id="element-180" data-mp="2mu3875htre80">
                <button type="button" aria-pressed={density === 'comfortable'} onClick={() => setDensity('comfortable')} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${density === 'comfortable' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-181" data-mp="2huidx8dlnu1m">
                  <Rows3Icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-182" data-mp="1z2piz8eyzyr9" />
                  Comfortable
                </button>
                <button type="button" aria-pressed={density === 'compact'} onClick={() => setDensity('compact')} className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${density === 'compact' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-183" data-mp="16gd5o36csy0h">
                  <ListIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-184" data-mp="7tgvzq1wv7xm" />
                  Compact
                </button>
              </div>
            </div>
          </div>

          {showFilters && <div id="ticket-filters" className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-185" data-mp="gp6bjigoh833">
              {activeTab === 'closed' ? <>
                  <MultiSelectFilter id="closed-created-by-filter" label="Created By" selectedValues={closedFilters.createdBy} options={closedCreators.map(creator => ({
              value: creator,
              label: creator
            }))} onChange={createdBy => setClosedFilters(currentFilters => ({
              ...currentFilters,
              createdBy
            }))} data-id="element-186" data-mp="2xb7gv796vo7w" />
                  <MultiSelectFilter id="closed-date-filter" label="Date Closed" selectedValues={closedFilters.closedDate} options={getClosedDateOptions(tickets)} onChange={closedDate => setClosedFilters(currentFilters => ({
              ...currentFilters,
              closedDate
            }))} data-id="element-187" data-mp="y2y0be3yqqrs" />
                  <div className="flex items-end" data-id="element-188" data-mp="k4hm723rtxzm">
                    <button type="button" onClick={() => setClosedFilters({
                createdBy: [],
                closedDate: []
              })} className="rounded-lg px-2 py-2 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-189" data-mp="k59eo422nflo">
                      Clear filters
                    </button>
                  </div>
                </> : <>
                  {activeTab === 'active' && <MultiSelectFilter id="ticket-status-filter" label="Status lanes" selectedValues={filters.status} options={activeStatuses.map(status => ({
              value: status,
              label: status
            }))} onChange={status => setFilters(currentFilters => ({
              ...currentFilters,
              status: status as Status[]
            }))} data-id="element-190" data-mp="1y5w52p0nqmyl" />}
                  <MultiSelectFilter id="ticket-priority-filter" label="Priority" selectedValues={filters.priority} options={['Critical', 'High', 'Medium', 'Low'].map(priority => ({
              value: priority,
              label: priority
            }))} onChange={priority => setFilters(currentFilters => ({
              ...currentFilters,
              priority: priority as Priority[]
            }))} data-id="element-191" data-mp="39rm79xs5c890" />
                  <MultiSelectFilter id="ticket-assignee-filter" label="Assignee" selectedValues={filters.assignee} options={assignees.map(assignee => ({
              value: assignee,
              label: assignee
            }))} onChange={assignee => setFilters(currentFilters => ({
              ...currentFilters,
              assignee
            }))} data-id="element-192" data-mp="8uadwrym6a3e" />
                  <div className="flex items-end" data-id="element-193" data-mp="1pay0xdvci7lm">
                    <button type="button" onClick={() => setFilters({
                status: [],
                priority: [],
                assignee: []
              })} className="rounded-lg px-2 py-2 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-194" data-mp="1p9mwdbu2svtg">
                      Clear filters
                    </button>
                  </div>
                </>}
            </div>}
        </header>

        <div className="overflow-x-auto bg-slate-50 p-3 sm:p-4" data-id="element-195" data-mp="1fcrzexdgb3ha">
          <div id={activeTab === 'active' ? 'active-kanban-board' : activeTab === 'types' ? 'type-cases-board' : 'closed-kanban-board'} className={`grid gap-2 ${visibleColumns.length === 1 ? 'max-w-md' : ''}`} style={{
          gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(140px, 1fr))`,
          minWidth: visibleColumns.length === 1 ? undefined : `${visibleColumns.length * 140}px`
        }} role="region" aria-label={activeTab === 'active' ? 'Active ticket kanban board' : activeTab === 'types' ? 'Type case board' : 'Closed cases'} tabIndex={0} data-id="element-196" data-mp="1fecque4gy6ax">
            {visibleColumns.map(column => {
            const columnTickets = ticketsByColumn[column];
            const visibleTickets = columnTickets.slice(0, visibleCounts[column]);
            const urgentCount = columnTickets.filter(ticket => isUrgent(ticket.priority)).length;
            const remainingCount = columnTickets.length - visibleTickets.length;
            const needsAttention = urgentCount >= 5 || columnTickets.length > 0 && urgentCount / columnTickets.length >= 0.4;
            const style = columnStyles[column];
            const isValidDropTarget = Boolean(draggedTicket) && getTicketColumn(draggedTicket, activeTab) !== column;
            const isDragOver = dragOverColumn === column && isValidDropTarget;
            return <section key={column} aria-labelledby={`column-${column.replaceAll(' ', '-').toLowerCase()}`} onDragOver={event => handleDragOver(event, column)} onDragEnter={event => handleDragOver(event, column)} onDragLeave={event => {
              if (event.currentTarget === event.target) setDragOverColumn(null);
            }} onDrop={event => handleDrop(event, column)} className={`min-w-0 rounded-lg border bg-white p-2 shadow-sm transition-all ${style.accent} ${isDragOver ? 'border-2 border-blue-500 bg-blue-50 ring-2 ring-blue-200 ring-offset-1' : isValidDropTarget ? 'border-dashed' : ''}`} data-id="element-197" data-mp="3rom375lugezz">
                  <div className="mb-2 border-b border-gray-100 pb-2" data-id="element-198" data-mp="1xenvpkz39o11">
                    <div className="flex items-start justify-between gap-1.5" data-id="element-199" data-mp="10qhmekr827gf">
                      <h3 id={`column-${column.replaceAll(' ', '-').toLowerCase()}`} title={column} className="flex min-w-0 items-center gap-1.5 text-xs font-bold leading-4 text-gray-800" data-id="element-200" data-mp="15nhut2bjnmap">
                        <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${style.dot}`} data-id="element-201" data-mp="hyyo2r857hkq" />
                        <span className="truncate" data-id="element-202" data-mp="3bidprwxbosrr">{column}</span>
                      </h3>
                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold ${style.count}`} aria-label={`${columnTickets.length} cases in ${column}`} data-id="element-203" data-mp="3bk6uv7saf4xn">
                        {columnTickets.length}
                      </span>
                    </div>
                    <div className="mt-1 flex min-w-0 items-center justify-between gap-1 text-[10px] leading-3" data-id="element-204" data-mp="3ug6h01yl3e9b">
                      <span className={`truncate ${urgentCount > 0 ? 'font-medium text-orange-700' : 'text-gray-500'}`} title={urgentCount > 0 ? `${urgentCount} urgent cases` : 'No urgent cases'} data-id="element-205" data-mp="3phd9cq5zahh2">
                        {urgentCount > 0 ? `${urgentCount} urgent` : 'Clear'}
                      </span>
                      {needsAttention && <AlertTriangleIcon aria-label="Attention needed" className="h-3.5 w-3.5 shrink-0 text-amber-600" data-id="element-206" data-mp="cyn7wdymwxsw" />}
                    </div>
                  </div>

                  <div className={density === 'compact' ? 'space-y-1.5' : 'space-y-2'} data-id="element-207" data-mp="mnkdxhclfgsz">
                    {columnTickets.length ? visibleTickets.map(ticket => {
                  const previousColumn = activeTab === 'types' ? caseTypes[caseTypes.indexOf(ticket.caseType as CaseType) - 1] : activeTab === 'active' ? workflowStatuses[workflowStatuses.indexOf(ticket.status) - 1] : undefined;
                  const nextColumn = activeTab === 'types' ? caseTypes[caseTypes.indexOf(ticket.caseType as CaseType) + 1] : activeTab === 'active' ? workflowStatuses[workflowStatuses.indexOf(ticket.status) + 1] : undefined;
                  const movementLabel = activeTab === 'types' ? 'type' : 'status';
                  const isCompact = density === 'compact';
                  return <article key={ticket.id} draggable tabIndex={0} aria-labelledby={`ticket-${ticket.id}`} aria-grabbed={draggingTicketId === ticket.id} aria-label={`${ticket.id}: ${ticket.subject}. Open ticket details or drag to change ${activeTab === 'types' ? 'case type' : 'status'}.`} onClick={() => setSelectedTicket(ticket)} onKeyDown={event => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      setSelectedTicket(ticket);
                    }
                  }} onDragStart={event => handleDragStart(event, ticket)} onDragEnd={clearDragState} className={`min-w-0 cursor-pointer rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:border-blue-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 active:cursor-grabbing ${isCompact ? 'p-2' : 'p-2.5'} ${draggingTicketId === ticket.id ? 'scale-[0.98] cursor-grabbing border-blue-300 opacity-50 shadow-none' : ''}`} data-id="element-208" data-mp="a8iq1c0yd1r7">
                            <div className={`flex items-start justify-between gap-1.5 ${isCompact ? 'mb-1.5' : 'mb-2'}`} data-id="element-209" data-mp="cvkgno9di88z">
                              <span className="truncate rounded border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-blue-700" data-id="element-210" data-mp="357z84wuuv72">
                                {ticket.id}
                              </span>
                              <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${priorityStyles[ticket.priority]}`} data-id="element-211" data-mp="3qap82vhe3uzp">
                                {ticket.priority}
                              </span>
                            </div>

                            <h4 id={`ticket-${ticket.id}`} title={ticket.subject} className="truncate text-xs font-semibold leading-4 text-gray-900" data-id="element-212" data-mp="19jh2b7jo3tr2">
                              {ticket.subject}
                            </h4>

                            <div className={`text-[10px] leading-3 text-gray-600 ${isCompact ? 'mt-1.5 space-y-1' : 'mt-2 space-y-1.5'}`} data-id="element-213" data-mp="38ta7bekpe8k1">
                              <div className="flex min-w-0 items-start gap-1.5" data-id="element-214" data-mp="33zfhruif2zd1">
                                <UserRoundIcon aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" data-id="element-215" data-mp="3hrc7jrwm4x69" />
                                <span className="min-w-0 truncate" data-id="element-216" data-mp="3rp09p2wjt6yg">
                                  <span className="font-medium text-gray-700" data-id="element-217" data-mp="3mtb86u0yxfzi">
                                    {ticket.submittedBy}
                                  </span>
                                  <span className="text-gray-500" data-id="element-218" data-mp="1ses7mwzzlmk4"> · {ticket.department}</span>
                                </span>
                              </div>
                              <div className="flex min-w-0 items-center gap-1.5" data-id="element-219" data-mp="1yu8s8aj077f7">
                                <span className="w-3 shrink-0 text-center text-xs font-bold text-gray-400" aria-hidden="true" data-id="element-220" data-mp="1ywbqtiy828vj">
                                  @
                                </span>
                                <span className="truncate font-medium text-gray-700" data-id="element-221" data-mp="1b2tme7s0q7z7">{ticket.assignedTo}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-gray-500" data-id="element-222" data-mp="1l2klkmrixb5m">
                                <CalendarIcon aria-hidden="true" className="h-3 w-3 shrink-0 text-gray-400" data-id="element-223" data-mp="1pu2gou6v8hio" />
                                <span data-id="element-224" data-mp="xc81yemx1pz7">{formatTicketAge(ticket.createdDate)}</span>
                              </div>
                            </div>

                            {activeTab !== 'closed' && <div className={`flex items-center justify-between border-t border-gray-100 ${isCompact ? 'mt-2 pt-1.5' : 'mt-2.5 pt-2'}`} data-id="element-225" data-mp="36n93lggiw1kk">
                                <button type="button" disabled={!previousColumn} onClick={event => {
                        event.stopPropagation();
                        if (activeTab === 'types') moveCaseType(ticket, -1);else moveTicket(ticket, -1);
                      }} aria-label={previousColumn ? `Move ${ticket.id} to ${previousColumn}` : `${ticket.id} is at the first ${movementLabel} stage`} className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-slate-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" data-id="element-226" data-mp="2wni8bck51ijf">
                                  <ArrowLeftIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-227" data-mp="2d2hzr27f0yvv" />
                                  <span className="sr-only" data-id="element-228" data-mp="2i0jj7o8r707v">Previous {movementLabel}</span>
                                </button>
                                <button type="button" disabled={!nextColumn} onClick={event => {
                        event.stopPropagation();
                        if (activeTab === 'types') moveCaseType(ticket, 1);else moveTicket(ticket, 1);
                      }} aria-label={nextColumn ? `Move ${ticket.id} to ${nextColumn}` : `${ticket.id} is at the final ${movementLabel} stage`} className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" data-id="element-229" data-mp="247tvnfdktn8n">
                                  <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-230" data-mp="bf5dxyqzkv7b" />
                                  <span className="sr-only" data-id="element-231" data-mp="1rt5aczqmi2pt">Next {movementLabel}</span>
                                </button>
                              </div>}
                          </article>;
                }) : <div className="rounded-md border border-dashed border-gray-200 bg-slate-50 px-2 py-6 text-center" data-id="element-232" data-mp="3l7ovadnkj3gt">
                        <p className={`text-[11px] font-medium ${style.empty}`} data-id="element-233" data-mp="3uzb09s9bc6b4">
                          No cases
                        </p>
                        <p className="mt-1 text-[10px] leading-3 text-gray-500" data-id="element-234" data-mp="3si1sw5wmlaa">
                          Drop a card here.
                        </p>
                      </div>}
                  </div>

                  {columnTickets.length > workingSetSize && <div className="mt-2 flex items-center gap-1 border-t border-gray-100 pt-2" data-id="element-235" data-mp="hrzvnpy24oqi">
                      {remainingCount > 0 && <button type="button" onClick={() => updateVisibleCount(column, visibleCounts[column] + workingSetSize)} className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-[10px] font-medium text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" aria-label={`Show ${Math.min(workingSetSize, remainingCount)} more ${column} tickets; ${remainingCount} remaining`} data-id="element-236" data-mp="2enntlqloedg8">
                          More <span className="text-gray-500" data-id="element-237" data-mp="2l09xnnosuxn6">({remainingCount})</span>
                        </button>}
                      {visibleTickets.length > workingSetSize && <button type="button" onClick={() => updateVisibleCount(column, workingSetSize)} className="rounded-md px-1.5 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" aria-label={`Show fewer ${column} tickets`} data-id="element-238" data-mp="19n7k71hzyo78">
                          Less
                        </button>}
                    </div>}
                </section>;
          })}
          </div>
        </div>

        <footer className="flex flex-col gap-1 border-t border-gray-100 bg-white px-5 py-4 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between" data-id="element-239" data-mp="8t5wbk3lu6c1">
          <span aria-live="polite" data-id="element-240" data-mp="3v3rvh694he3w">
            Showing {filteredTickets.length} of{' '}
            {activeTab === 'active' ? activeTicketCount : activeTab === 'types' ? typeCaseCount : closedTicketCount}{' '}
            {activeTab === 'active' ? 'active cases' : activeTab === 'types' ? 'type cases' : 'closed cases'}{' '}
            · {activeTab === 'closed' ? 'most recently closed first' : sortMode === 'triage' ? 'triage sort' : 'newest first'}{' '}
            · {density} density
          </span>
          <span className="text-xs text-gray-400" data-id="element-241" data-mp="1iu9vysnou8e7">
            {activeTab === 'closed' ? 'Filter closed cases by their creator or close date.' : activeTab === 'types' ? 'Type cases are managed separately from the workflow Kanban.' : `Each lane starts with ${workingSetSize} cases to keep the working queue scannable.`}
          </span>
        </footer>
      </section>

      {selectedTicket && <TicketDetailModal ticket={selectedTicket} assignees={assignees} onClose={() => setSelectedTicket(null)} onUpdateTicket={updateTicket} data-id="element-242" data-mp="z3euadfp1ctu" />}
    </>;
}
function closeOpenHoldPeriods(periods: Ticket['onHoldPeriods'], endedAt: string): Ticket['onHoldPeriods'] {
  return periods?.map(period => period.endedAt ? period : {
    ...period,
    endedAt
  });
}
function isCaseType(column: BoardColumn): column is CaseType {
  return caseTypes.includes(column as CaseType);
}
function getTicketColumn(ticket: Ticket, view: TicketView): BoardColumn {
  return view === 'types' && ticket.caseType ? ticket.caseType : ticket.status;
}
type MultiSelectFilterProps = {
  id: string;
  label: string;
  selectedValues: string[];
  options: MultiSelectOption[];
  onChange: (values: string[]) => void;
};
function MultiSelectFilter({
  id,
  label,
  selectedValues,
  options,
  onChange
}: MultiSelectFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const visibleOptions = options.filter(option => option.label.toLocaleLowerCase().includes(searchQuery.trim().toLocaleLowerCase()));
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    searchInputRef.current?.focus();
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);
  const toggleOption = (value: string) => {
    onChange(selectedValues.includes(value) ? selectedValues.filter(selectedValue => selectedValue !== value) : [...selectedValues, value]);
  };
  const clearSelection = () => onChange([]);
  const selectedLabels = options.filter(option => selectedValues.includes(option.value)).map(option => option.label);
  const triggerText = selectedLabels.length ? selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selected` : `All ${label.toLocaleLowerCase()}`;
  return <div ref={containerRef} className="relative min-w-0" data-id="element-243" data-mp="2l7hhph34r1sf">
      <label htmlFor={`${id}-trigger`} className="mb-1 block text-xs font-medium text-gray-500" data-id="element-244" data-mp="2l7zoh7nf4yel">
        {label}
      </label>
      <button id={`${id}-trigger`} type="button" aria-expanded={isOpen} aria-controls={`${id}-menu`} aria-haspopup="listbox" onClick={() => {
      setIsOpen(open => !open);
      setSearchQuery('');
    }} className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-900 transition-colors hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-245" data-mp="3dwezzk7ccyl6">
        <span className="min-w-0 truncate font-medium" data-id="element-246" data-mp="3rvo4vnq3k69u">{triggerText}</span>
        <span className="flex shrink-0 items-center gap-1.5" data-id="element-247" data-mp="3rud3tvo1dcmf">
          {selectedValues.length > 0 && <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-bold text-blue-700" data-id="element-248" data-mp="811gj9hyu0ul">
              {selectedValues.length}
            </span>}
          <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-249" data-mp="d278h53sxk32" />
        </span>
      </button>

      {isOpen && <div id={`${id}-menu`} role="dialog" aria-label={`Select ${label}`} className="absolute z-30 mt-1 w-full min-w-[15rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" onKeyDown={event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
      }
    }} data-id="element-250" data-mp="qzvtqe1s6t8n">
          <div className="border-b border-gray-100 p-2" data-id="element-251" data-mp="19pveioql9m69">
            <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 focus-within:ring-2 focus-within:ring-blue-500" data-id="element-252" data-mp="19qn2bddy2flz">
              <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" data-id="element-253" data-mp="31spq8003txo8" />
              <span className="sr-only" data-id="element-254" data-mp="3fjl09fmcakte">Search {label}</span>
              <input ref={searchInputRef} type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder={`Search ${label.toLocaleLowerCase()}`} className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400" data-id="element-255" data-mp="3fl5vz8ke49su" />
            </label>
          </div>
          <div role="listbox" aria-multiselectable="true" className="max-h-56 overflow-y-auto p-1.5" data-id="element-256" data-mp="3ug2dkce3a9il">
            {visibleOptions.length ? visibleOptions.map(option => {
          const isSelected = selectedValues.includes(option.value);
          return <button key={option.value} type="button" role="option" aria-selected={isSelected} onClick={() => toggleOption(option.value)} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSelected ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-slate-50'}`} data-id="element-257" data-mp="1pz5qaew2v88c">
                    <span aria-hidden="true" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`} data-id="element-258" data-mp="xc1jq5hc6lug">
                      {isSelected && <CheckIcon className="h-3 w-3" data-id="element-259" data-mp="91cbinpdc5sv" />}
                    </span>
                    <span className="min-w-0 truncate" data-id="element-260" data-mp="44c4oakp2z0p">{option.label}</span>
                  </button>;
        }) : <p className="px-2.5 py-5 text-center text-sm text-gray-500" data-id="element-261" data-mp="3rg3gy6l1fctg">
                No matching options.
              </p>}
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2" data-id="element-262" data-mp="3milbiq68bfq7">
            <span className="text-xs text-gray-500" data-id="element-263" data-mp="3hgckw0gtwpr9">
              {selectedValues.length ? `${selectedValues.length} selected` : 'All included'}
            </span>
            <button type="button" onClick={clearSelection} disabled={selectedValues.length === 0} className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-264" data-mp="38i0vg1rp7y0e">
              <XIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-265" data-mp="2tlk9szbgwts3" />
              Clear
            </button>
          </div>
        </div>}
    </div>;
}
function getClosedDateOptions(tickets: Ticket[]): MultiSelectOption[] {
  return [...new Set(tickets.filter(ticket => ticket.status === 'Closed' && !ticket.caseType && ticket.closedDate).map(ticket => ticket.closedDate?.slice(0, 10) ?? ''))].filter(Boolean).sort((firstDate, secondDate) => secondDate.localeCompare(firstDate)).map(date => ({
    value: date,
    label: new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(`${date}T12:00:00`))
  }));
}
function isUrgent(priority: Priority) {
  return priority === 'Critical' || priority === 'High';
}
function formatCreatedDate(createdDate: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(createdDate));
}
function formatTicketAge(createdDate: string) {
  const elapsedMilliseconds = Date.now() - new Date(createdDate).getTime();
  const elapsedDays = Math.max(0, Math.floor(elapsedMilliseconds / 86400000));
  if (elapsedDays < 1) return 'today';
  if (elapsedDays < 7) return `${elapsedDays}d`;
  if (elapsedDays < 30) return `${Math.floor(elapsedDays / 7)}w`;
  if (elapsedDays < 365) return `${Math.floor(elapsedDays / 30)}mo`;
  return `${Math.floor(elapsedDays / 365)}y`;
}